import React, { useState, useEffect, useCallback } from 'react';
import { FaceLivenessDetectorCore } from '@aws-amplify/ui-react-liveness';
import { ThemeProvider } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import './App.css';

// Access Bank brand theme
const accessBankTheme = {
    name: 'access-bank-theme',
    tokens: {
        colors: {
            background: {
                primary: { value: '#ffffff' },
                secondary: { value: '#f8f9fa' },
            },
            font: {
                primary: { value: '#26343b' },
                secondary: { value: '#5a6670' },
            },
            brand: {
                primary: {
                    '10': { value: '#fff4e5' },
                    '20': { value: '#ffe8cc' },
                    '40': { value: '#ffbf66' },
                    '60': { value: '#ff9933' },
                    '80': { value: '#ff8200' },
                    '90': { value: '#e67300' },
                    '100': { value: '#cc6600' },
                },
            },
            border: {
                primary: { value: '#e0e0e0' },
            },
        },
        components: {
            button: {
                primary: {
                    backgroundColor: { value: '#ff8200' },
                    color: { value: '#ffffff' },
                    _hover: {
                        backgroundColor: { value: '#e67300' },
                    },
                    _active: {
                        backgroundColor: { value: '#cc6600' },
                    },
                },
            },
        },
    },
};

function App() {
    const [sessionId, setSessionId] = useState(null);
    const [returnUrl, setReturnUrl] = useState(null);
    const [credentials, setCredentials] = useState(null);
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState(null);
    const [step, setStep] = useState('intro'); // 'intro' or 'verify'

    useEffect(() => {
        initializeSession();
    }, []);

    const initializeSession = async () => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const sessionDataParam = urlParams.get('sessionData');
            const returnUrlParam = urlParams.get('returnUrl');

            if (!sessionDataParam) {
                setError('This verification link is invalid or has expired.');
                setStatus('error');
                return;
            }

            let sessionData;
            try {
                // First try to parse as URL-encoded JSON (production format)
                // Check if it looks like JSON (starts with { after decoding)
                const urlDecoded = decodeURIComponent(sessionDataParam);
                if (urlDecoded.startsWith('{')) {
                    // Handle \u002B escape sequences for + signs
                    const fixedJson = urlDecoded.replace(/\\u002B/gi, '+');
                    sessionData = JSON.parse(fixedJson);
                } else {
                    // Try base64 decoding (localhost format)
                    const decodedData = atob(sessionDataParam);
                    sessionData = JSON.parse(decodedData);
                }
            } catch (e) {
                console.error('Failed to parse session data:', e);
                setError('This verification link is invalid or has expired.');
                setStatus('error');
                return;
            }

            if (!sessionData.sessionId || !sessionData.credentials) {
                setError('This verification link is invalid or has expired.');
                setStatus('error');
                return;
            }

            // Check if credentials are already expired
            if (new Date(sessionData.credentials.expiration) < new Date()) {
                setError('This verification link has expired.');
                setStatus('error');
                return;
            }

            setSessionId(sessionData.sessionId);
            // Treat empty string as no returnUrl
            const returnUrlValue = returnUrlParam || sessionData.returnUrl;
            setReturnUrl(returnUrlValue && returnUrlValue.trim() !== '' ? returnUrlValue : null);
            setCredentials({
                accessKeyId: sessionData.credentials.accessKeyId,
                secretAccessKey: sessionData.credentials.secretAccessKey,
                sessionToken: sessionData.credentials.sessionToken,
                expiration: sessionData.credentials.expiration,
                region: sessionData.region || sessionData.credentials.region || 'af-south-1'
            });
            setStatus('ready');

        } catch (err) {
            console.error('Initialization error:', err);
            setError('This verification session is no longer valid.');
            setStatus('error');
        }
    };

    const handleStartVerification = () => {
        setStep('verify');
    };

    const credentialProvider = useCallback(async () => {
        if (!credentials) {
            throw new Error('Credentials not available');
        }
        return {
            accessKeyId: credentials.accessKeyId,
            secretAccessKey: credentials.secretAccessKey,
            sessionToken: credentials.sessionToken,
            expiration: new Date(credentials.expiration)
        };
    }, [credentials]);

    const handleAnalysisComplete = async () => {
        setStatus('success');

        // Only redirect if returnUrl is specified
        if (returnUrl) {
            const separator = returnUrl.includes('?') ? '&' : '?';
            const redirectUrl = `${returnUrl}${separator}livenessResult=success&sessionId=${sessionId}`;

            setTimeout(() => {
                window.location.href = redirectUrl;
            }, 2500);
        }
        // If no returnUrl, just stay on success page (thank you page)
    };

    const handleError = (error) => {
        console.error('Liveness error:', error);

        if (error?.error?.message?.includes('user cancelled') ||
            error?.message?.includes('user cancelled')) {
            setError('Verification was cancelled.');
        } else {
            setError('This verification session has expired or is no longer valid.');
        }
        setStatus('error');
    };

    const handleBackClick = () => {
        if (returnUrl) {
            const separator = returnUrl.includes('?') ? '&' : '?';
            window.location.href = `${returnUrl}${separator}livenessResult=cancelled`;
        }
    };

    // Loading state
    if (status === 'loading') {
        return (
            <div className="app-container">
                <div className="status-card">
                    <div className="loading-spinner"></div>
                    <h2>Getting ready...</h2>
                    <p>Please wait a moment.</p>
                </div>
            </div>
        );
    }

    // Error state
    if (status === 'error') {
        return (
            <div className="app-container">
                <div className="status-card error">
                    <div className="error-icon">✕</div>
                    <h2>Session Expired</h2>
                    <p>{error}</p>
                    <p className="expired-note">Please request a new verification link to try again.</p>
                    {returnUrl && (
                        <button onClick={handleBackClick} className="btn btn-primary">
                            Go Back
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Success state
    if (status === 'success') {
        return (
            <div className="app-container">
                <div className="status-card success">
                    <div className="success-icon">✓</div>
                    <h2>Verification Complete</h2>
                    <p>Your identity has been verified successfully.</p>
                    {returnUrl ? (
                        <p className="redirect-text">Redirecting you back...</p>
                    ) : (
                        <p className="redirect-text">Thank you! You may now close this window.</p>
                    )}
                </div>
            </div>
        );
    }

    // Ready state - show intro or Face Liveness component
    if (step === 'intro') {
        return (
            <div className="app-container">
                <div className="intro-card">
                    <div className="intro-icon">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                        </svg>
                    </div>
                    <h1>Identity Verification</h1>
                    <p className="intro-subtitle">We need to verify that you are a real person</p>

                    <div className="intro-steps">
                        <div className="intro-step">
                            <span className="step-number">1</span>
                            <span className="step-text">Position your face in the camera frame</span>
                        </div>
                        <div className="intro-step">
                            <span className="step-number">2</span>
                            <span className="step-text">Follow the on-screen instructions</span>
                        </div>
                        <div className="intro-step">
                            <span className="step-number">3</span>
                            <span className="step-text">Hold still while we verify</span>
                        </div>
                    </div>

                    <div className="intro-requirements">
                        <h3>Before you start</h3>
                        <ul>
                            <li>Make sure you're in a well-lit area</li>
                            <li>Remove glasses or hats if possible</li>
                            <li>Ensure your camera is working</li>
                        </ul>
                    </div>

                    <div className="intro-warning">
                        You have <strong>one attempt</strong> to complete this verification.
                        If unsuccessful, you'll need to request a new link.
                    </div>

                    <button onClick={handleStartVerification} className="btn btn-primary btn-large">
                        Start Verification
                    </button>

                    {returnUrl && (
                        <button onClick={handleBackClick} className="btn btn-link">
                            Cancel
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <ThemeProvider theme={accessBankTheme}>
            <div className="app-container">
                <div className="liveness-wrapper">
                    <div className="liveness-container">
                        {credentials && (
                            <FaceLivenessDetectorCore
                                sessionId={sessionId}
                                region={credentials.region}
                                onAnalysisComplete={handleAnalysisComplete}
                                onError={handleError}
                                disableStartScreen={true}
                                config={{
                                    credentialProvider: credentialProvider
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </ThemeProvider>
    );
}

export default App;